import type { ModelInfo } from "./types.js";

// ── Default model registry ───────────────────────────────────
// Real pricing as of early 2026. Updated via `fathom research`.

const DEFAULT_MODELS: ModelInfo[] = [
  // ── Anthropic ──────────────────────────────────────────────
  {
    id: "claude-opus-4-6",
    name: "Claude Opus 4.6",
    provider: "anthropic",
    tier: "frontier",
    inputPerMTok: 15.0,
    outputPerMTok: 75.0,
    cacheReadPerMTok: 1.5,
    batchDiscount: 0.5,
    capabilities: ["code", "reasoning", "creative", "architecture"],
  },
  {
    id: "claude-sonnet-4-6",
    name: "Claude Sonnet 4.6",
    provider: "anthropic",
    tier: "mid",
    inputPerMTok: 3.0,
    outputPerMTok: 15.0,
    cacheReadPerMTok: 0.3,
    batchDiscount: 0.5,
    capabilities: ["code", "reasoning", "creative", "fast"],
  },
  {
    id: "claude-haiku-4-5",
    name: "Claude Haiku 4.5",
    provider: "anthropic",
    tier: "economy",
    inputPerMTok: 1.0,
    outputPerMTok: 5.0,
    cacheReadPerMTok: 0.1,
    batchDiscount: 0.5,
    capabilities: ["code", "fast"],
  },

  // ── OpenAI ─────────────────────────────────────────────────
  {
    id: "gpt-4o",
    name: "GPT-4o",
    provider: "openai",
    tier: "mid",
    inputPerMTok: 2.5,
    outputPerMTok: 10.0,
    capabilities: ["code", "reasoning", "creative"],
  },
  {
    id: "gpt-4o-mini",
    name: "GPT-4o Mini",
    provider: "openai",
    tier: "economy",
    inputPerMTok: 0.15,
    outputPerMTok: 0.6,
    capabilities: ["code", "fast"],
  },
  {
    id: "o1",
    name: "o1",
    provider: "openai",
    tier: "frontier",
    inputPerMTok: 15.0,
    outputPerMTok: 60.0,
    capabilities: ["reasoning", "code", "architecture"],
  },
  {
    id: "o3-mini",
    name: "o3-mini",
    provider: "openai",
    tier: "mid",
    inputPerMTok: 1.1,
    outputPerMTok: 4.4,
    capabilities: ["reasoning", "code", "fast"],
  },

  // ── Google ─────────────────────────────────────────────────
  {
    id: "gemini-2.0-flash",
    name: "Gemini 2.0 Flash",
    provider: "google",
    tier: "economy",
    inputPerMTok: 0.1,
    outputPerMTok: 0.4,
    capabilities: ["code", "fast"],
  },
  {
    id: "gemini-2.0-pro",
    name: "Gemini 2.0 Pro",
    provider: "google",
    tier: "mid",
    inputPerMTok: 1.25,
    outputPerMTok: 5.0,
    capabilities: ["code", "reasoning", "creative"],
  },

  // ── Ollama (local) ──────────────────────────────────────────
  {
    id: "ollama/llama3.3",
    name: "Llama 3.3 70B",
    provider: "ollama",
    tier: "mid",
    inputPerMTok: 0,
    outputPerMTok: 0,
    capabilities: ["code", "reasoning"],
  },
  {
    id: "ollama/llama3.1",
    name: "Llama 3.1 8B",
    provider: "ollama",
    tier: "economy",
    inputPerMTok: 0,
    outputPerMTok: 0,
    capabilities: ["code", "fast"],
  },
  {
    id: "ollama/codellama",
    name: "Code Llama 34B",
    provider: "ollama",
    tier: "mid",
    inputPerMTok: 0,
    outputPerMTok: 0,
    capabilities: ["code"],
  },
  {
    id: "ollama/mistral",
    name: "Mistral 7B",
    provider: "ollama",
    tier: "economy",
    inputPerMTok: 0,
    outputPerMTok: 0,
    capabilities: ["code", "fast"],
  },
  {
    id: "ollama/mixtral",
    name: "Mixtral 8x7B",
    provider: "ollama",
    tier: "mid",
    inputPerMTok: 0,
    outputPerMTok: 0,
    capabilities: ["code", "reasoning"],
  },
  {
    id: "ollama/deepseek-coder-v2",
    name: "DeepSeek Coder V2",
    provider: "ollama",
    tier: "mid",
    inputPerMTok: 0,
    outputPerMTok: 0,
    capabilities: ["code", "reasoning"],
  },
  {
    id: "ollama/qwen2.5-coder",
    name: "Qwen 2.5 Coder 32B",
    provider: "ollama",
    tier: "mid",
    inputPerMTok: 0,
    outputPerMTok: 0,
    capabilities: ["code", "reasoning"],
  },

  // ── OpenRouter ──────────────────────────────────────────────
  {
    id: "openrouter/auto",
    name: "OpenRouter Auto",
    provider: "openrouter",
    tier: "mid",
    inputPerMTok: 2.0,
    outputPerMTok: 8.0,
    capabilities: ["code", "reasoning"],
  },
];

/**
 * Return a fresh copy of the built-in model registry.
 */
export function getDefaultModels(): ModelInfo[] {
  return DEFAULT_MODELS.map((m) => ({ ...m }));
}

/**
 * Look up a model by its canonical id.
 */
export function getModelById(
  id: string,
  models: ModelInfo[] = DEFAULT_MODELS,
): ModelInfo | undefined {
  return models.find((m) => m.id === id);
}

/**
 * Return all models from a given provider.
 */
export function getModelsByProvider(
  provider: string,
  models: ModelInfo[] = DEFAULT_MODELS,
): ModelInfo[] {
  return models.filter((m) => m.provider === provider);
}

/**
 * Return all models in a given tier.
 */
export function getModelsByTier(
  tier: "frontier" | "mid" | "economy",
  models: ModelInfo[] = DEFAULT_MODELS,
): ModelInfo[] {
  return models.filter((m) => m.tier === tier);
}
